import { ReactNode } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives";
import { Editions, ProductKeys } from "@atlassiansox/asc-stargate";

import { Adg4Button } from "@/components/adg4/button/src/button";
import { useCrossFlowFrontendTouchpoint } from "@/components/cross-flow/touchpoint/use-touchpoint/index";
import TryIntent from "@/components/try-intent/src/try-intent";
import { wacToken } from "@/libraries/design/foundations";
import { useLocalizedHref } from "@/libraries/web-framework/src/localization/use-localized-href";

export const Header = () => {
    const { openCrossFlow } = useCrossFlowFrontendTouchpoint({
        productKey: ProductKeys.JIRA_SOFTWARE,
        edition: Editions.FREE,
        redirectUrl: "/try/cloud/signup?bundle=jira-software"
    });

    const { formatMessage } = useIntl();

    const subHeading = formatMessage({
        id: "jira-product-tour.hero-header.sub-header-text",
        defaultMessage:
            "The only project management tool you need to plan and track work across every team."
    });

    const buttonText = formatMessage({
        id: "jira-product-tour.hero-header.button-text",
        defaultMessage: "Get Jira free"
    });

    const mainHeading = formatMessage(
        {
            id: "jira-product-tour.hero-header.header-text",
            defaultMessage:
                "<strong>Great outcomes</strong> start with <em>Jira</em>"
        },
        {
            strong: (text: ReactNode) => (
                <strong css={mainHeadingStrongStyles}>{text}</strong>
            ),
            em: (text: ReactNode) => <em css={mainHeadingEmStyles}>{text}</em>
        }
    );

    const handleClick = (e: React.MouseEvent) => {
        if (openCrossFlow) {
            openCrossFlow(e);
        }
    };

    return (
        <div css={sectionStyles}>
            <h1 css={mainHeadingStyles}>{mainHeading}</h1>
            <div css={subheadingStyles}>{subHeading}</div>
            <TryIntent
                intentType="button"
                containerId="heroSection"
                productKey="jira-software.ondemand"
                edition="free"
            >
                <Adg4Button
                    onClick={handleClick}
                    href={useLocalizedHref(
                        "/try/cloud/signup?bundle=jira-software"
                    )}
                    variant="primary"
                    size="large"
                    color="blue"
                    dataEventProps={{
                        "data-product-key": "jira",
                        "data-event": "clicked",
                        "data-event-component": "button",
                        "data-event-container": "heroSection",
                        "data-label": "get-jira-free",
                        "data-label-english": "Get Jira Free",
                        "data-track-event": "jira-pt.hero-cta.get-jira-free"
                    }}
                >
                    {buttonText}
                </Adg4Button>
            </TryIntent>
        </div>
    );
};

const sectionStyles = css({
    display: "grid",
    justifyItems: "center",
    paddingBlockStart: "64px",
    [media.below.lg]: {
        paddingBlockStart: "32px"
    }
});

const subheadingStyles = css({
    fontFamily: wacToken("font.charlie.text"),
    fontWeight: "400",
    fontSize: "20px",
    lineHeight: "28px",
    textAlign: "center",
    color: wacToken("color.primary.black"),
    padding: "0 10% 40px",
    [media.below.md]: {
        fontSize: "16px",
        lineHeight: "26px",
        padding: "0 20% 40px"
    }
});

const mainHeadingStyles = css({
    color: wacToken("color.primary.black"),
    fontSize: "36px",
    fontWeight: "normal",
    lineHeight: 1.1,
    margin: "0 12px 12px",
    textAlign: "center",
    [media.only.md]: {
        fontSize: "48px"
    },
    [media.above.lg]: {
        fontSize: "66px"
    }
});

const mainHeadingStrongStyles = css({
    fontWeight: "bold",
    display: "block",
    [media.above.sm]: {
        display: "inline"
    }
});

const mainHeadingEmStyles = css({
    fontStyle: "normal",
    position: "relative",
    [":after"]: {
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='93' height='15' fill='none'%3E%3Cpath fill='%23FCA700' d='M93 12.932c-1.99-3.316.348-9.177-2.864-12.344-6.496-2.118-7.694 10.2-11.93 11.8-1.927-3.112.112-7.825-2.313-11.07.116.078.49.488.53.542-6.37-7.096-8.484 9.605-13.305 10.591C61.095 9.036 64.51-.225 58.305.17c-3.913.058-8.41 16.61-10.67 10.972l-.008.079-.029-.029c.22 4.017 3.69-.444 4.311-1.704-5.314 9.94-5.02-2.611-5.562-7.17 1.06 1.205.621 4.517.982 5.943-.05-17.405-9.074-2.114-13.194 4.017-2.922-.15-.804-7.23-2.102-9.554.075.497.564 3.311.083 3.859-.128-2.06-.012-6.417-3.166-5.89.323-.676 1.906 0 2.089.31-6.608-3.17-7.482 10.305-12.651 11.486-1.505-3.506.402-7.991-1.704-11.444C10.562-5.58 3.904 22.022 2.781 8.546 2.437 5.827 3.436 1.064 0 .596c.543.32 1.758 1.177 1.691 2.512-.091-.141-.178-.282-.27-.415.933 3.553-.638 7.358 1.025 10.703C6.76 17.504 11.1-.104 15.474 1.657c1.799 2.28.51 6.044.966 8.771-.034-.087-.2-.65-.228-.725 1.198 13.074 9.645-5.696 8.82-2.898 10.616-16.812.336 16.792 11.839 5.82 2.71-2.275 4.377-11.01 7.954-9.845.481 4.502.717 17.46 7.354 9.078 2.5-2.806 3.494-8.77 7.192-9.6 1.96 3.52-1.335 15.806 6.271 11.826-.074.03-.145.062-.224.091 3.084-1.077 6.222-15.56 9.455-11.196 1.248 2.836-1.102 10.997 3.706 11.242 4.207-.352 8.51-18.247 11.474-10.114.539 1.94.298 10.512 2.939 8.812l.008.013Z'/%3E%3C/svg%3E")`,
        backgroundRepeat: "no-repeat",
        backgroundSize: "contain",
        content: "''",
        height: "20px",
        insetBlockEnd: -14,
        insetInlineEnd: -2,
        insetInlineStart: -4,
        position: "absolute",
        [media.only.md]: {
            insetBlockEnd: -10
        },
        [media.above.lg]: {
            insetBlockEnd: -6
        }
    }
});
